import { CopyAll, Refresh } from '@mui/icons-material';
import { CircularProgress, IconButton, TextField } from '@mui/material';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { ToastSeverity, toastStateSelector } from 'state';
import { FormFieldProps } from '.';
import { getCalculatedFieldValue } from 'api';
import { useParams } from 'react-router-dom';
import { GeneralIds } from 'types/types';
import { useEffect, useState } from 'react';

export function ReportField({
  field,
  error,
  name,
  sx,
  value,
  isImported,
  helperText,
  onChange,
}: FormFieldProps): JSX.Element {
  const setToast = useSetRecoilState(toastStateSelector);
  const { t } = useTranslation();
  const { formId, visitId } = useParams<GeneralIds>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCalculatedValue = async () => {
      if (value) return;
      setLoading(true);
      try {
        const { value: calculatedValue } = await getCalculatedFieldValue({
          formId: formId ?? '',
          visitId: visitId ?? '',
          fieldTemplateId: field.id,
        });
        onChange({
          target: { name, value: calculatedValue },
        });
      } catch (error) {
        console.error('Error fetching calculated field value:', error);
        setToast({
          isOpen: true,
          message: t('forms.reportFetchError'),
          severity: ToastSeverity.error,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchCalculatedValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCopyToClipboard = async () => {
    try {
      // Try the modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value as string);
      } else {
        // Fallback for older browsers or when Clipboard API fails
        const textArea = document.createElement('textarea');
        textArea.value = value as string;

        // Make the textarea out of viewport
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);

        textArea.focus();
        textArea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (!successful) {
          throw new Error('Copy command was unsuccessful');
        }
      }

      setToast({
        isOpen: true,
        message: t('forms.reportCopied'),
        severity: ToastSeverity.success,
      });
    } catch (error) {
      console.error('Failed to copy text: ', error);
      setToast({
        isOpen: true,
        message: t('forms.reportCopyError'),
        severity: ToastSeverity.error,
      });
    }
  };

  const onRefresh = async () => {
    setLoading(true);
    const { value: calculatedValue } = await getCalculatedFieldValue({
      formId: formId ?? '',
      visitId: visitId ?? '',
      fieldTemplateId: field.id,
    });

    onChange({
      target: { name, value: calculatedValue },
    });

    setLoading(false);
  };

  return (
    <Box
      position={'relative'}
      pt={7}
      display={'flex'}
      flexDirection={'column'}
      width={'100%'}
    >
      <Box
        display={'flex'}
        justifyContent={'end'}
        position={'absolute'}
        top={0}
        right={0}
      >
        <IconButton onClick={onRefresh}>
          <Refresh />
        </IconButton>
        <IconButton onClick={onCopyToClipboard}>
          <CopyAll />
        </IconButton>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <TextField
          value={value ?? ''}
          onChange={onChange}
          name={name}
          multiline
          fullWidth
        />
      )}
    </Box>
  );
}
